import { useCallback, useEffect, useRef, useState } from "react";
import { normalizeStreamType, type StationV2, type UrlV2 } from "@orvis/core";
import { OrvisCast, hasNative, type CastState } from "../native.js";
import { trackEvent } from "../analytics.js";

/**
 * Send the current station to a TV / speaker:
 *   - Android app → Google Cast (OrvisCast plugin, Default Media Receiver).
 *     Once a device connects we load the station's active URL there and
 *     the caller pauses local playback (`casting` = true).
 *   - iOS / Safari → AirPlay through WebKit's own route picker; the OS
 *     moves playback, nothing else to coordinate.
 * Neither exists on desktop Chrome/Firefox (the browser's own "Cast tab"
 * menu covers that), nor on Android TV, which is itself a cast target.
 */
type AirPlayElement = HTMLMediaElement & { webkitShowPlaybackTargetPicker?: () => void };

function contentType(url: UrlV2): string {
  switch (normalizeStreamType(url.type)) {
    case "MP3":
      return "audio/mpeg";
    case "AAC":
      return "audio/aac";
    default:
      return "application/x-mpegurl";
  }
}

export function useCast(opts: {
  station: StationV2 | undefined;
  url: UrlV2 | undefined;
  kind: "video" | "audio";
  el: HTMLMediaElement | null;
  subtitle?: string;
}): { available: boolean; state: CastState; device?: string; casting: boolean; open: () => void } {
  const { station, url, kind, el, subtitle } = opts;
  const [state, setState] = useState<CastState>("unavailable");
  const [device, setDevice] = useState<string | undefined>();
  const [airplay, setAirplay] = useState(false);
  const loadedFor = useRef<string | undefined>();
  const native = hasNative("OrvisCast");

  useEffect(() => {
    if (!native) return;
    void OrvisCast.isAvailable()
      .then((r) => {
        setState(r.available ? r.state : "unavailable");
        setDevice(r.device);
      })
      .catch(() => {});
    const handle = OrvisCast.addListener("castState", (e) => {
      setState(e.state);
      setDevice(e.device);
      if (e.state !== "connected") loadedFor.current = undefined;
    });
    return () => void handle.then((h) => h.remove());
  }, [native]);

  // Connected → (re)load whatever is selected now.
  useEffect(() => {
    if (!native || state !== "connected" || !station || !url) return;
    const id = `${station.id ?? station.name}|${url.value}`;
    if (loadedFor.current === id) return;
    loadedFor.current = id;
    trackEvent("cast_load", { content_type: kind, item_id: station.id ?? station.name });
    void OrvisCast.loadMedia({
      url: url.value,
      contentType: contentType(url),
      kind,
      title: station.name,
      subtitle,
      image: station.logo || undefined,
    }).catch((err) => console.warn("[cast] load failed", err));
  }, [native, state, station, url, kind, subtitle]);

  // AirPlay availability (WebKit only).
  useEffect(() => {
    if (native || !el || !("WebKitPlaybackTargetAvailabilityEvent" in window)) return;
    const onAvail = (e: Event) => setAirplay((e as Event & { availability?: string }).availability === "available");
    el.addEventListener("webkitplaybacktargetavailabilitychanged", onAvail);
    return () => el.removeEventListener("webkitplaybacktargetavailabilitychanged", onAvail);
  }, [native, el]);

  const open = useCallback(() => {
    if (native) {
      void OrvisCast.showPicker().catch(() => {});
    } else {
      (el as AirPlayElement | null)?.webkitShowPlaybackTargetPicker?.();
    }
  }, [native, el]);

  const available = native ? state !== "unavailable" : airplay;
  return { available, state, device, casting: native && state === "connected", open };
}
